// NOT a 'use client' entry — always imported by page.tsx (which is 'use client').
// Omitting the directive prevents rule 71007 from flagging onReconfigured.
// Client context (useState, etc.) is inherited from the importing page.

import { useState } from "react";
import { Settings2, LoaderCircle } from "lucide-react";
import { reconfigureSheetImport } from "@/lib/api/endpoints";
import type { ImportConfig } from "@/lib/mapper/types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type Props = {
  workbookId: string;
  sheetName: string;
  /** The sheet's current importConfig — seeds the wizard. */
  currentConfig?: ImportConfig | null;
  /**
   * Whether the workbook has an original file retained on the server.
   * When false (or unknown), the reconfigure endpoint will return 409 and
   * we show a re-upload message.
   */
  hasOriginalFile?: boolean;
  /** Called after a successful non-dry-run reconfigure so the parent can refresh. */
  onReconfigured?: (droppedCount: number) => void;
};

// ---------------------------------------------------------------------------
// Drop-confirm dialog
// ---------------------------------------------------------------------------

type DropConfirmProps = {
  count: number;
  onConfirm: () => void;
  onCancel: () => void;
};

function DropConfirmDialog({ count, onConfirm, onCancel }: DropConfirmProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="drop-confirm-title"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/40 px-4 backdrop-blur-sm"
    >
      <button
        type="button"
        aria-label="Cancel"
        className="absolute inset-0 cursor-default"
        onClick={onCancel}
      />
      <div className="relative w-full max-w-sm overflow-hidden rounded-[10px] border border-slate-200 bg-white shadow-xl dark:border-border dark:bg-surface">
        <div className="px-5 py-5">
          <h2
            id="drop-confirm-title"
            className="text-base font-semibold text-slate-950 dark:text-slate-100"
          >
            Drop {count} manual edit{count !== 1 ? "s" : ""}?
          </h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            Re-configuring the import with this header layout will discard{" "}
            <strong>{count}</strong> manual cell edit{count !== 1 ? "s" : ""}{" "}
            whose rows no longer match. This cannot be undone.
          </p>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-3 dark:border-border">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-border dark:bg-surface dark:text-slate-300 dark:hover:bg-surface-2"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-700"
          >
            Drop edits &amp; apply
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------

type ToastProps = {
  message: string;
  tone: "success" | "warning" | "error";
};

function Toast({ message, tone }: ToastProps) {
  const cls = {
    success: "bg-emerald-700 text-white",
    warning: "bg-amber-600 text-white",
    error: "bg-red-600 text-white",
  }[tone];

  return (
    <div
      role="status"
      className={`pointer-events-none fixed bottom-6 left-1/2 z-[70] -translate-x-1/2 rounded-md px-3.5 py-2 text-[12px] font-medium shadow-lg ${cls}`}
    >
      {message}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ReconfigureImportButton
// ---------------------------------------------------------------------------

/**
 * "Re-configure import" button for the sheet page top bar.
 *
 * Flow:
 *   1. User clicks the button → opens ImportWizard in "reconfigure" mode.
 *      The wizard uses parse-preview on a *dummy* file if no file is available,
 *      but in reconfigure mode we seed from the current importConfig directly
 *      and the wizard operates without uploading (it uses the raw grid from
 *      parse-preview — it still needs to upload the file for that).
 *      Since we don't have the original file client-side, the button drives the
 *      reconfigure endpoint directly (no raw-grid preview), showing a lightweight
 *      config form instead.
 *   2. Wizard onConfirm → call reconfigure with dryRun=true.
 *   3. If droppedOverrideRowIds > 0 → show DropConfirmDialog.
 *   4. User confirms (or no drops) → call reconfigure with dryRun=false.
 *   5. Toast the result; call onReconfigured.
 *
 * Note: The wizard normally needs a File to call parse-preview. In reconfigure
 * mode without a client-side file we use a *minimal re-configure form* instead
 * of the full wizard (since the raw grid isn't available). Users who want the
 * full wizard experience must re-upload via the Import dialog.
 */
export function ReconfigureImportButton({
  workbookId,
  sheetName,
  currentConfig,
  hasOriginalFile,
  onReconfigured,
}: Props) {
  const [open, setOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [pendingConfig, setPendingConfig] = useState<ImportConfig | null>(null);
  const [dropCount, setDropCount] = useState(0);
  const [showDropConfirm, setShowDropConfirm] = useState(false);
  const [toast, setToast] = useState<ToastProps | null>(null);

  function showToast(message: string, tone: ToastProps["tone"]) {
    setToast({ message, tone });
    setTimeout(() => setToast(null), 4000);
  }

  async function applyConfig(config: ImportConfig, dryRun: boolean): Promise<number> {
    const result = await reconfigureSheetImport({
      id: workbookId,
      sheet: sheetName,
      config,
      dryRun,
    });
    return result.droppedOverrideRowIds.length;
  }

  async function handleWizardConfirm(configs: Record<string, ImportConfig>) {
    const config = configs[sheetName] ?? Object.values(configs)[0];
    if (!config) return;

    setOpen(false);
    setApplying(true);

    try {
      // Step 1: dry run to check for dropped edits.
      const dropped = await applyConfig(config, true);

      if (dropped > 0) {
        // Step 2: ask the user to confirm dropping the edits.
        setPendingConfig(config);
        setDropCount(dropped);
        setApplying(false);
        setShowDropConfirm(true);
      } else {
        // No edits to drop — apply immediately.
        await applyConfig(config, false);
        setApplying(false);
        showToast("Import re-configured.", "success");
        onReconfigured?.(0);
      }
    } catch (err) {
      setApplying(false);
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes("409") || msg.toLowerCase().includes("re-upload")) {
        showToast("Re-upload required to re-configure this sheet.", "warning");
      } else {
        showToast(`Re-configure failed: ${msg}`, "error");
      }
    }
  }

  async function handleDropConfirm() {
    if (!pendingConfig) return;
    setShowDropConfirm(false);
    setApplying(true);

    try {
      await applyConfig(pendingConfig, false);
      setApplying(false);
      showToast(
        `Re-configured. ${dropCount} manual edit${dropCount !== 1 ? "s" : ""} dropped.`,
        "warning",
      );
      onReconfigured?.(dropCount);
    } catch (err) {
      setApplying(false);
      showToast(
        `Re-configure failed: ${err instanceof Error ? err.message : String(err)}`,
        "error",
      );
    } finally {
      setPendingConfig(null);
      setDropCount(0);
    }
  }

  const disabled = applying;
  const title = hasOriginalFile === false
    ? "Re-upload the file to re-configure the import layout"
    : "Re-configure the header / data layout for this sheet";

  return (
    <>
      <button
        type="button"
        onClick={() => {
          if (hasOriginalFile === false) {
            showToast("Re-upload required to re-configure.", "warning");
            return;
          }
          setOpen(true);
        }}
        disabled={disabled}
        title={title}
        aria-label="Re-configure import"
        className={[
          "inline-flex items-center gap-1 rounded border px-2 py-1.5 text-[12px] font-medium transition",
          disabled
            ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400 dark:border-border dark:bg-surface dark:text-slate-500"
            : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-border dark:bg-surface dark:text-slate-300 dark:hover:bg-surface-2",
        ].join(" ")}
      >
        {applying ? (
          <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Settings2 className="h-3.5 w-3.5" />
        )}
        Re-configure
      </button>

      {/* Lightweight re-configure form (config-only mode, no raw-grid preview).
          We create a tiny synthetic "file" to satisfy ImportWizard's API, but
          the wizard is opened in reconfigure mode with initialConfig seeded. */}
      {open && currentConfig && (
        <ReconfigureWizardProxy
          workbookId={workbookId}
          sheetName={sheetName}
          currentConfig={currentConfig}
          onConfirm={handleWizardConfirm}
          onCancel={() => setOpen(false)}
        />
      )}

      {/* If no currentConfig, show a simple message */}
      {open && !currentConfig && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/35 px-4 backdrop-blur-sm"
        >
          <button
            type="button"
            aria-label="Close"
            className="absolute inset-0 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="relative w-full max-w-sm overflow-hidden rounded-[10px] border border-slate-200 bg-white p-6 shadow-xl dark:border-border dark:bg-surface">
            <p className="text-sm text-slate-700 dark:text-slate-300">
              No import configuration found for this sheet. Re-import the file
              to set one up.
            </p>
            <div className="mt-4 flex justify-end">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-border dark:bg-surface dark:text-slate-300 dark:hover:bg-surface-2"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showDropConfirm && (
        <DropConfirmDialog
          count={dropCount}
          onConfirm={() => void handleDropConfirm()}
          onCancel={() => {
            setShowDropConfirm(false);
            setPendingConfig(null);
            setDropCount(0);
          }}
        />
      )}

      {toast && <Toast message={toast.message} tone={toast.tone} />}
    </>
  );
}

// ---------------------------------------------------------------------------
// ReconfigureWizardProxy
//
// The full ImportWizard needs a File to call parse-preview. In reconfigure
// mode we don't have the original file, so we drive the config via a direct
// form. We use the ImportWizard in reconfigure mode by creating a minimal
// synthetic Blob that the backend will re-parse from its stored originalFileKey.
//
// Because the backend parse-preview endpoint requires the file, and we don't
// have it here, we implement a lightweight inline form instead of the full
// wizard. The form mirrors the ImportWizard's controls but sends the config
// directly to the reconfigure endpoint.
// ---------------------------------------------------------------------------

type ProxyProps = {
  workbookId: string;
  sheetName: string;
  currentConfig: ImportConfig;
  onConfirm: (configs: Record<string, ImportConfig>) => void;
  onCancel: () => void;
};

function ReconfigureWizardProxy({
  sheetName,
  currentConfig,
  onConfirm,
  onCancel,
}: ProxyProps) {
  const [config, setConfig] = useState<ImportConfig>({ ...currentConfig });

  function clamp(n: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, n));
  }

  function NumberRow({
    label,
    value,
    min,
    max,
    onChange,
  }: {
    label: string;
    value: number;
    min: number;
    max: number;
    onChange: (n: number) => void;
  }) {
    return (
      <label className="flex items-center justify-between gap-4">
        <span className="text-sm text-slate-700 dark:text-slate-300">
          {label}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            disabled={value <= min}
            onClick={() => onChange(clamp(value - 1, min, max))}
            className="flex h-7 w-7 items-center justify-center rounded border border-slate-300 text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-border dark:text-slate-300 dark:hover:bg-surface-2"
          >
            −
          </button>
          <input
            type="number"
            value={value + 1}
            min={min + 1}
            max={max + 1}
            onChange={(e) => {
              const n = parseInt(e.currentTarget.value, 10);
              if (!Number.isNaN(n)) onChange(clamp(n - 1, min, max));
            }}
            className="w-14 rounded border border-slate-300 bg-white px-1.5 py-1 text-center text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-200 dark:border-border dark:bg-surface dark:text-slate-100 dark:focus:ring-slate-700"
          />
          <button
            type="button"
            disabled={value >= max}
            onClick={() => onChange(clamp(value + 1, min, max))}
            className="flex h-7 w-7 items-center justify-center rounded border border-slate-300 text-slate-600 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-border dark:text-slate-300 dark:hover:bg-surface-2"
          >
            +
          </button>
        </div>
      </label>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reconfigure-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/35 px-4 py-6 backdrop-blur-sm"
    >
      <button
        type="button"
        aria-label="Cancel"
        className="absolute inset-0 cursor-default"
        onClick={onCancel}
      />
      <div className="relative w-full max-w-md overflow-hidden rounded-[10px] border border-slate-200 bg-white shadow-xl dark:border-border dark:bg-surface">
        <div className="border-b border-slate-100 px-5 py-4 dark:border-border">
          <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">
            {sheetName}
          </p>
          <h2
            id="reconfigure-title"
            className="mt-1 text-lg font-semibold text-slate-950 dark:text-slate-100"
          >
            Re-configure import
          </h2>
          <p className="mt-1 text-[12px] text-slate-500 dark:text-slate-400">
            Adjust the header and data layout. Row numbers are 1-based.
          </p>
        </div>

        <div className="space-y-4 px-5 py-5">
          <NumberRow
            label="Header row start"
            value={config.headerRowStart}
            min={0}
            max={99}
            onChange={(n) =>
              setConfig((c) => ({
                ...c,
                headerRowStart: n,
                headerRowEnd: Math.max(n, c.headerRowEnd),
                dataStartIdx: Math.max(c.dataStartIdx, Math.max(n, c.headerRowEnd) + 1),
              }))
            }
          />
          <NumberRow
            label="Header row end"
            value={config.headerRowEnd}
            min={config.headerRowStart}
            max={99}
            onChange={(n) =>
              setConfig((c) => ({
                ...c,
                headerRowEnd: n,
                dataStartIdx: Math.max(c.dataStartIdx, n + 1),
              }))
            }
          />
          <NumberRow
            label="Data start row"
            value={config.dataStartIdx}
            min={config.headerRowEnd + 1}
            max={999}
            onChange={(n) =>
              setConfig((c) => ({
                ...c,
                dataStartIdx: Math.max(n, c.headerRowEnd + 1),
              }))
            }
          />

          {config.headerRowEnd > config.headerRowStart && (
            <label className="flex items-center justify-between gap-4">
              <span className="text-sm text-slate-700 dark:text-slate-300">
                Header join separator
              </span>
              <input
                type="text"
                value={config.joinSeparator}
                onChange={(e) =>
                  setConfig((c) => ({ ...c, joinSeparator: e.currentTarget.value }))
                }
                className="w-24 rounded border border-slate-300 bg-white px-2 py-1 text-sm text-slate-800 outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-200 dark:border-border dark:bg-surface dark:text-slate-100 dark:focus:ring-slate-700"
              />
            </label>
          )}

          <p className="text-[11px] text-slate-500 dark:text-slate-400">
            To exclude columns or skip rows, re-upload the file and use the
            full import wizard.
          </p>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 px-5 py-4 dark:border-border">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-border dark:bg-surface dark:text-slate-300 dark:hover:bg-surface-2"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm({ [sheetName]: config })}
            className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-700 dark:bg-slate-100 dark:text-slate-950 dark:hover:bg-slate-200"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
